import React, { useEffect, useSyncExternalStore } from 'react';
import { Button, Paper, Box, Slide, Alert, AlertTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import { createTranslationField } from '@/app/utils/i18nUtils.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import { checkIsOutdated, getOutdatedState, subscribeOutdated } from '@/app/utils/buildVersion.ts';

/**
 * VersionChecker component
 * Periodically checks for a new application version and displays a notification
 * when a new version is available.
 *
 * The running build's hash is baked into the bundle (CURRENT_BUILD_HASH), so there is no startup
 * fetch that could race a deploy. Outdated state is shared with the error boundaries and the API
 * client via buildVersion.ts: when the outdated build was detected while handling an error (5xx,
 * crash), the toast switches to stronger "reload required" copy.
 */
const VersionChecker = () => {
  const { outdated, detectedViaError } = useSyncExternalStore(subscribeOutdated, getOutdatedState);
  const { t } = useTranslation(I18nNamespace.Common);
  const i18n = createTranslationField<I18nCommonNs>();
  // Check interval based on environment:
  // - Production: check every hour (3600000 ms)
  // - Local: check every 10 minutes (600000 ms)
  // - Development: check every 5 minutes (300000 ms)
  const checkInterval =
    process.env.NODE_ENV === 'production' ? 3600000 : process.env.NODE_ENV === 'local' ? 600000 : 300000;

  // Periodic poll, plus a (throttled) check whenever the tab becomes visible again
  useEffect(() => {
    const interval = setInterval(() => void checkIsOutdated('poll'), checkInterval);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkIsOutdated('poll');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkInterval]);

  // Show update notification if outdated
  if (outdated) {
    return (
      <Slide direction="up" in={outdated}>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            maxWidth: '320px',

            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              },
              '50%': {
                transform: 'scale(1.05)',
                boxShadow: '0 4px 25px rgba(0, 0, 0, 0.4)'
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }
            }
          }}
        >
          <Alert severity={detectedViaError ? 'warning' : 'info'} sx={{ p: 2 }}>
            <AlertTitle>
              {detectedViaError ? t(i18n.versionChecker.reloadRequired) : t(i18n.versionChecker.newVersionAvailable)}
            </AlertTitle>
            {detectedViaError
              ? t(i18n.versionChecker.reloadRequiredBody)
              : t(i18n.versionChecker.updateToGetLatestFeatures)}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                color={detectedViaError ? 'warning' : 'info'}
                size="small"
                variant="contained"
                onClick={() => window.location.reload()}
              >
                {t(i18n.versionChecker.updateNow)}
              </Button>
            </Box>
          </Alert>
        </Paper>
      </Slide>
    );
  }

  return null;
};

export default VersionChecker;
